import React from 'react';
import { Divider, Popconfirm, Tooltip, Button } from 'antd';
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  WarningOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import columns from './columns';
import SimpleResourceLandingLayout from '../../components/layouts/SimpleResourceLandingLayout';
import { routes, subRoutes } from '../../utils/constants/routes';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import ValidationQuoteModal from './ValidationQuoteModal';
import { AnalyzeModal } from './AnalyzeModal';
import useQuotesContext from './QuotesContext';

const iconSize = 18;

const ListQuotes = () => {
  const { t } = useTranslation();

  const { dispatchAPI, user } = useAuthContext();
  const { message } = useErrorMessage();
  const navigate = useNavigate();

  const { handleOpenModal, forceRefresh, setForceRefresh } = useQuotesContext();

  const contractWithSupplier = [
    'MRPWK',
    'MRPCM',
    'MRPML',
    'RCPCM',
    'RCPWK',
    'RCPML'
  ];

  const deleteQuote = async (id) => {
    try {
      await dispatchAPI('DELETE', { url: `/contracts/${id}` });
      setForceRefresh(!forceRefresh);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getAssoParam = (local) => {
    if (local) return '?assoT=localRc';
    return '?assoT=rc';
  };

  const getMRPParam = (type, activities) => {
    const suffix = `${
      contractWithSupplier.includes(type)
        ? `&sup=${type.slice(-2).toLowerCase()}`
        : ''
    }`;
    if (activities[0] === 'Activité de bureau') {
      return `?mrpT=office${suffix}`;
    }
    return `?mrpT=any${suffix}`;
  };

  const customActionColumn = {
    key: 'action',
    align: 'right',
    fixed: 'right',
    width: 150,
    // eslint-disable-next-line react/prop-types
    render: ({ key, type, status, local, activities }) => (
      <div style={{ float: 'right' }}>
        <Tooltip placement="top" title="Ouvrir">
          <Link
            to={{
              pathname: `${routes.QUOTES}${subRoutes.QUOTES.QUOTES_SAVED}/show/${key}`
            }}
            style={{
              color: 'var(--clientColor)'
            }}
          >
            <EyeOutlined style={{ fontSize: iconSize }} />
          </Link>
        </Tooltip>
        <Divider orientation="vertical" />
        {status && status !== 'DELETE' ? (
          <Tooltip placement="top" title="Modifier">
            <Button
              type="link"
              onClick={() =>
                navigate(
                  `${routes.QUOTES}/${
                    contractWithSupplier.includes(type)
                      ? // eslint-disable-next-line react/prop-types
                        type.substring(0, 3).toLowerCase()
                      : // eslint-disable-next-line react/prop-types
                        type.toLowerCase()
                  }/edit/${key}/${
                    type === 'ASSOCIATION' ? getAssoParam(local) : ''
                  }${
                    // eslint-disable-next-line no-nested-ternary
                    type === 'MRP' ||
                    type === 'MRPWK' ||
                    type === 'MRPCM' ||
                    type === 'MRPML'
                      ? getMRPParam(type, activities)
                      : ['RCPWK', 'RCPCM', 'RCPML'].includes(type)
                        ? // eslint-disable-next-line react/prop-types
                          `?sup=${type.slice(-2).toLowerCase()}`
                        : ''
                  }`
                )
              }
              style={{
                color: 'var(--clientColor)',
                padding: '1px'
              }}
            >
              <EditOutlined style={{ fontSize: iconSize }} />
            </Button>
          </Tooltip>
        ) : (
          <Tooltip placement="top" title="Supprimé">
            <EditOutlined
              style={{ fontSize: iconSize, cursor: 'default' }}
              disabled
            />
          </Tooltip>
        )}
        <Divider orientation="vertical" />
        {status && status !== 'DELETE' ? (
          <Tooltip placement="top" title="Valider">
            <CheckOutlined
              onClick={() => handleOpenModal(key, type)}
              style={{ color: 'var(--addColor)', fontSize: iconSize }}
              type="validate"
            />
          </Tooltip>
        ) : (
          <CloseOutlined style={{ color: 'red' }} />
        )}
        <Divider orientation="vertical" />
        <Tooltip placement="top" title="Annuler">
          <Popconfirm
            title={t('datatable.column.action.delete.title')}
            okText={t('datatable.column.action.delete.ok')}
            okButtonProps={{ type: 'danger' }}
            cancelText={t('datatable.column.action.delete.cancel')}
            onConfirm={async () => {
              setForceRefresh(!forceRefresh);
              await deleteQuote(key);
            }}
            icon={<WarningOutlined />}
          >
            <DeleteOutlined
              style={{ color: '#b51010', fontSize: iconSize }}
              type="delete"
            />
          </Popconfirm>
        </Tooltip>
      </div>
    )
  };

  return (
    <>
      <ValidationQuoteModal />
      <AnalyzeModal />
      <SimpleResourceLandingLayout
        forceRefresh={forceRefresh}
        withCreateButton={false}
        resourceName="quotes"
        dataToFetch="contracts"
        extraQuery={`status=QUOTE&!exemption${
          user?.permissionManagement?.quotations ? '' : `&user=${user?._id}`
        }`}
        populate="insurer"
        columns={[...columns(), customActionColumn]}
        customActionColumn
        scroll={{ x: 1300 }}
      />
    </>
  );
};

export default ListQuotes;
